import React, { useState, useEffect } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import { useTranslations } from 'use-intl';
import { toTranslationKey, tForComponent } from '@/utils/translations';
import QuestionnaireInput from './QuestionnaireInput';
import { formatValue } from './utils';
import EmptySection from './EmptySection';
import { useUpdateQuestionnaireStore } from '@/stores/useUpdateQuestionnaire';
import {
	MonthlyHouseExpenses as MonthlyHouseExpensesType,
	Maybe
} from '@/graphql/__generated__/credit-requests';
import QuestionnaireEditableInput from './QuestionnaireEditableInput';

interface MonthlyHouseExpensesProps {
	data: Maybe<MonthlyHouseExpensesType> | undefined;
	title: string;
	isEditing?: boolean;
	inputErrors?: Record<string, string>;
}

enum inputErrorMapping {
	rent = 'rent',
	electricBill = 'electric_bill',
	internetBill = 'internet_bill',
	waterBill = 'water_bill',
	groceries = 'groceries',
	tuitions = 'tuitions',
	familyExpenses = 'family_expenses'
}

const MonthlyHouseExpenses: React.FC<MonthlyHouseExpensesProps> = ({
	data,
	title,
	isEditing,
	inputErrors
}) => {
	const t = useTranslations('MicrocreditRequestQuestionnaire');
	const { monthlyHouseExpenses, updateMonthlyHouseExpenses } = useUpdateQuestionnaireStore();
	const [formData, setFormData] = useState(monthlyHouseExpenses as MonthlyHouseExpensesType);

	useEffect(() => {
		setFormData(monthlyHouseExpenses);
	}, [monthlyHouseExpenses]);

	const handleChange = (key: string, value: any) => {
		setFormData((prevData: any) => ({
			...prevData,
			[key]: value
		}));
		updateMonthlyHouseExpenses({ [key]: value });
	};
	const editableFields = () => {
		return (
			<>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'rent'}
						value={formData['rent'] || data?.rent || ''}
						onChange={(newValue) => handleChange('rent', newValue)}
						error={inputErrors?.[inputErrorMapping.rent] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'electricBill'}
						value={formData['electricBill'] || data?.electricBill || ''}
						onChange={(newValue) => handleChange('electricBill', newValue)}
						error={inputErrors?.[inputErrorMapping.electricBill] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'internetBill'}
						value={formData['internetBill'] || data?.internetBill || ''}
						onChange={(newValue) => handleChange('internetBill', newValue)}
						error={inputErrors?.[inputErrorMapping.internetBill] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'waterBill'}
						value={formData['waterBill'] || data?.waterBill || ''}
						onChange={(newValue) => handleChange('waterBill', newValue)}
						error={inputErrors?.[inputErrorMapping.waterBill] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'groceries'}
						value={formData['groceries'] || data?.groceries || ''}
						onChange={(newValue) => handleChange('groceries', newValue)}
						error={inputErrors?.[inputErrorMapping.groceries] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'tuitions'}
						value={formData['tuitions'] || data?.tuitions || ''}
						onChange={(newValue) => handleChange('tuitions', newValue)}
						error={inputErrors?.[inputErrorMapping.tuitions] || ''}
						type="money"
					/>
				</div>
				<div className="w-1/2 px-3 py-1">
					<QuestionnaireEditableInput
						title={'familyExpenses'}
						value={formData['familyExpenses'] || data?.familyExpenses || ''}
						onChange={(newValue) => handleChange('familyExpenses', newValue)}
						error={inputErrors?.[inputErrorMapping.familyExpenses] || ''}
						type="money"
					/>
				</div>
			</>
		);
	};

	return (
		<div className="flex flex-col gap-1 p-4 bg-white rounded-lg w-full">
			<div className={`flex flex-row items-center mb-4`}>
				<div className="grow">
					<Typography weight="semibold">{t(toTranslationKey(title))}</Typography>
				</div>
			</div>
			{data ? (
				<div className="flex flex-row w-full">
					<div className="flex flex-row flex-wrap w-full">
						{isEditing ? (
							editableFields()
						) : (
							<>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'rent'} value={formatValue(data.rent, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'electricBill'}
										value={formatValue(data.electricBill, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'internetBill'}
										value={formatValue(data.internetBill, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'waterBill'} value={formatValue(data.waterBill, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'groceries'} value={formatValue(data.groceries, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput title={'tuitions'} value={formatValue(data.tuitions, t)} />
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'familyExpenses'}
										value={formatValue(data.familyExpenses, t)}
									/>
								</div>
								<div className="w-1/2 px-3 py-1">
									<QuestionnaireInput
										title={'totalHouseExpenses'}
										value={formatValue(data.totalHouseExpenses, t)}
									/>
								</div>
							</>
						)}
					</div>
				</div>
			) : (
				<EmptySection t={tForComponent(t)} />
			)}
		</div>
	);
};

export default MonthlyHouseExpenses;
